import React, { useState } from "react";
import "./AnalysisResults.css";
import KnowledgeGraph from "./KnowledgeGraph";
import ImpactSummaryTab from "./ImpactSummaryTab";

const AnalysisResults = ({ analysisData, sessionId, isConversion = false }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedItems, setExpandedItems] = useState({});
  const [impactViewMode, setImpactViewMode] = useState("table"); // 'table' or 'cards'

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const renderImpactAnalysis = () => {
    if (!analysisData?.impact_analysis)
      return <div>No impact analysis data available</div>;

    return (
      <div className="impact-analysis">
        <div className="impact-header-section">
          <h3>🔍 Impact Analysis Results</h3>
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${
                impactViewMode === "table" ? "active" : ""
              }`}
              onClick={() => setImpactViewMode("table")}
            >
              📊 Table View
            </button>
            <button
              className={`view-mode-btn ${
                impactViewMode === "cards" ? "active" : ""
              }`}
              onClick={() => setImpactViewMode("cards")}
            >
              🗂️ Card View
            </button>
          </div>
        </div>

        {impactViewMode === "table" ? (
          <div className="impact-table-container">
            <table className="impact-table">
              <thead>
                <tr>
                  <th>Function Name</th>
                  <th>Impact Level</th>
                  <th>Confidence</th>
                  <th>Affected Functions</th>
                  <th>Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {analysisData.impact_analysis.map((item, index) => (
                  <tr key={index} className="impact-table-row">
                    <td className="function-name-cell">
                      <strong>{item.function_name}</strong>
                    </td>
                    <td className="impact-level-cell">
                      <span
                        className={`impact-badge ${item.impact_level
                          .toLowerCase()
                          .replace(" ", "-")}`}
                      >
                        {item.impact_level}
                      </span>
                    </td>
                    <td className="confidence-cell">
                      <div className="confidence-bar">
                        <div
                          className="confidence-fill"
                          style={{ width: `${item.confidence * 100}%` }}
                        ></div>
                        <span className="confidence-text">
                          {Math.round(item.confidence * 100)}%
                        </span>
                      </div>
                    </td>
                    <td className="affected-functions-cell">
                      {item.affected_functions &&
                      item.affected_functions.length > 0 ? (
                        <div className="affected-functions-list">
                          {item.affected_functions.map((func, idx) => (
                            <span key={idx} className="affected-function-tag">
                              {typeof func === "string"
                                ? func
                                : func.name ||
                                  func.label ||
                                  JSON.stringify(func)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-affected">None</span>
                      )}
                    </td>
                    <td className="reasoning-cell">
                      <div className="reasoning-text">
                        {item.reasoning || "N/A"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="impact-cards-container">
            {analysisData.impact_analysis.map((item, index) => (
              <div key={index} className="impact-item">
                <div
                  className="impact-header"
                  onClick={() => toggleExpanded(`impact-${index}`)}
                >
                  <span className="impact-level">{item.impact_level}</span>
                  <span className="function-name">{item.function_name}</span>
                  <span className="confidence">
                    Confidence: {Math.round(item.confidence * 100)}%
                  </span>
                  <span className="expand-icon">
                    {expandedItems[`impact-${index}`] ? "▼" : "▶"}
                  </span>
                </div>
                {expandedItems[`impact-${index}`] && (
                  <div className="impact-details">
                    {/* Show Pre-Extracted Log & Logic Classification */}
                    {item.impact_classification && (
                      <div className="impact-classification-section">
                        <h4>📊 Impact Classification (Pre-Extracted):</h4>
                        <div className="classification-stats">
                          <div className="stat-item">
                            <span className="stat-label">Impact Type:</span>
                            <span className="stat-value">
                              {item.impact_classification.impact_type ||
                                "Unknown"}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Log Statements:</span>
                            <span className="stat-value log-count">
                              {item.impact_classification.log_count || 0}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">
                              Logic Structures:
                            </span>
                            <span className="stat-value logic-count">
                              {item.impact_classification.logic_count || 0}
                            </span>
                          </div>
                        </div>

                        {/* Show Actual Logs */}
                        {item.impact_classification.logs &&
                          item.impact_classification.logs.length > 0 && (
                            <div className="logs-section">
                              <h5>📝 Log Statements Found:</h5>
                              <ul className="logs-list">
                                {item.impact_classification.logs
                                  .slice(0, 5)
                                  .map((log, idx) => (
                                    <li key={idx} className="log-item">
                                      <span className="log-type">
                                        [{log.type}]
                                      </span>
                                      <span className="log-statement">
                                        {log.statement}
                                      </span>
                                      <span className="log-line">
                                        Line {log.line_in_function}
                                      </span>
                                    </li>
                                  ))}
                                {item.impact_classification.logs.length > 5 && (
                                  <li className="more-items">
                                    ... and{" "}
                                    {item.impact_classification.logs.length - 5}{" "}
                                    more logs
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                        {/* Show Actual Logic Structures */}
                        {item.impact_classification.logic_structures &&
                          item.impact_classification.logic_structures.length >
                            0 && (
                            <div className="logic-section">
                              <h5>🔀 Logic Structures Found:</h5>
                              <ul className="logic-list">
                                {item.impact_classification.logic_structures
                                  .slice(0, 5)
                                  .map((logic, idx) => (
                                    <li key={idx} className="logic-item">
                                      <span className="logic-type">
                                        [{logic.type}]
                                      </span>
                                      <span className="logic-statement">
                                        {logic.statement}
                                      </span>
                                      <span className="logic-line">
                                        Line {logic.line_in_function}
                                      </span>
                                    </li>
                                  ))}
                                {item.impact_classification.logic_structures
                                  .length > 5 && (
                                  <li className="more-items">
                                    ... and{" "}
                                    {item.impact_classification.logic_structures
                                      .length - 5}{" "}
                                    more logic structures
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    )}

                    <div className="reasoning">
                      <h4>Reasoning:</h4>
                      <p>{item.reasoning}</p>
                    </div>
                    {item.affected_functions &&
                      item.affected_functions.length > 0 && (
                        <div className="affected-functions">
                          <h4>Affected Functions:</h4>
                          <ul>
                            {item.affected_functions.map((func, idx) => (
                              <li key={idx}>
                                {typeof func === "string"
                                  ? func
                                  : func.name ||
                                    func.label ||
                                    JSON.stringify(func)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    {item.suggestions && (
                      <div className="suggestions">
                        <h4>Suggestions:</h4>
                        <p>{item.suggestions}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDependencyAnalysis = () => {
    // Check for call_graph data (what the backend actually provides)
    const callGraph = analysisData?.call_graph;
    if (!callGraph || Object.keys(callGraph).length === 0) {
      return <div>No dependency analysis data available</div>;
    }

    return (
      <div className="dependency-analysis">
        <h3>🔗 Dependency Analysis</h3>
        {Object.entries(callGraph).map(
          ([functionName, dependencies], index) => (
            <div key={index} className="dependency-item">
              <div
                className="dependency-header"
                onClick={() => toggleExpanded(`dep-${index}`)}
              >
                <span className="function-name">{functionName}</span>
                <span className="dependency-count">
                  {dependencies?.length || 0} dependencies
                </span>
                <span className="expand-icon">
                  {expandedItems[`dep-${index}`] ? "▼" : "▶"}
                </span>
              </div>
              {expandedItems[`dep-${index}`] &&
                dependencies &&
                dependencies.length > 0 && (
                  <div className="dependency-details">
                    <h4>Dependencies:</h4>
                    <ul>
                      {dependencies.map((dependency, idx) => (
                        <li key={idx}>
                          <span className="dep-name">
                            {typeof dependency === "string"
                              ? dependency
                              : dependency.name ||
                                dependency.label ||
                                JSON.stringify(dependency)}
                          </span>
                          <span className="dep-type">(function)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          )
        )}
      </div>
    );
  };

  const renderPythonConversion = () => {
    if (!analysisData?.conversion_results)
      return <div>No conversion data available</div>;

    return (
      <div className="python-conversion">
        <h3> Python Conversion Results</h3>
        {Object.entries(analysisData.conversion_results).map(
          ([filename, result]) => (
            <div key={filename} className="conversion-item">
              <div
                className="conversion-header"
                onClick={() => toggleExpanded(`conv-${filename}`)}
              >
                <span className="file-name">{filename}</span>
                <span className="conversion-status">
                  {result.success ? "✅ Converted" : "❌ Failed"}
                </span>
                <span className="expand-icon">
                  {expandedItems[`conv-${filename}`] ? "▼" : "▶"}
                </span>
              </div>
              {expandedItems[`conv-${filename}`] && (
                <div className="conversion-details">
                  {result.python_code && (
                    <div className="python-code">
                      <h4>Python Code:</h4>
                      <pre className="code-block">
                        <code>{result.python_code}</code>
                      </pre>
                    </div>
                  )}
                  {result.explanations && (
                    <div className="conversion-explanations">
                      <h4>Conversion Notes:</h4>
                      <p>{result.explanations}</p>
                    </div>
                  )}
                  {result.warnings && result.warnings.length > 0 && (
                    <div className="conversion-warnings">
                      <h4>Warnings:</h4>
                      <ul>
                        {result.warnings.map((warning, idx) => (
                          <li key={idx} className="warning">
                            ⚠️{" "}
                            {typeof warning === "string"
                              ? warning
                              : warning.message ||
                                warning.text ||
                                JSON.stringify(warning)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  const renderOverview = () => {
    // Debug: Log the actual data structure
    console.log("=== DEBUG: analysisData structure ===");
    console.log("Full analysisData:", analysisData);
    console.log("analysisData keys:", Object.keys(analysisData || {}));
    console.log("impact_analysis:", analysisData?.impact_analysis);
    console.log("call_graph:", analysisData?.call_graph);
    console.log("old_kg_triplets:", analysisData?.old_kg_triplets);
    console.log("new_kg_triplets:", analysisData?.new_kg_triplets);

    // Get stats from the actual backend response structure
    const impactAnalysis = analysisData?.impact_analysis || [];
    const callGraph = analysisData?.call_graph || {};
    const oldKgTriplets = analysisData?.old_kg_triplets || [];
    const newKgTriplets = analysisData?.new_kg_triplets || [];

    console.log("=== DEBUG: Extracted arrays ===");
    console.log("impactAnalysis length:", impactAnalysis.length);
    console.log("callGraph keys:", Object.keys(callGraph));
    console.log("oldKgTriplets length:", oldKgTriplets.length);
    console.log("newKgTriplets length:", newKgTriplets.length);

    // Calculate stats directly from impact analysis data
    const logicImpact = impactAnalysis.filter(
      (item) => item.impact_level === "Logic Impact"
    ).length;
    const logImpact = impactAnalysis.filter(
      (item) => item.impact_level === "Log Impact"
    ).length;
    const noImpact = impactAnalysis.filter(
      (item) => item.impact_level === "No Impact"
    ).length;
    const totalFunctions = impactAnalysis.length;

    const stats = {
      totalFunctions: totalFunctions,
      logicImpact: logicImpact,
      logImpact: logImpact,
      noImpact: noImpact,
      impactItems: impactAnalysis.length,
      dependencyItems: Object.keys(callGraph).length,
      kgNodes: Math.max(oldKgTriplets.length, newKgTriplets.length),
      conversionItems: analysisData?.python_conversion?.enabled
        ? Object.keys(
            analysisData.python_conversion?.old_version?.converted_files || {}
          ).length
        : 0,
    };

    return (
      <div className="overview">
        <h3> Analysis Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.totalFunctions}</div>
            <div className="stat-label">Functions Analyzed</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.logicImpact}</div>
            <div className="stat-label">Logic Impact</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.logImpact}</div>
            <div className="stat-label">Log Impact</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.noImpact}</div>
            <div className="stat-label">No Impact</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.impactItems}</div>
            <div className="stat-label">Impact Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.dependencyItems}</div>
            <div className="stat-label">Dependencies</div>
          </div>
        </div>

        {/* Impact Summary - Display using ImpactSummaryTab component */}
        {/* Check for impact_summary, paragraph_summary, or summary_text */}
        {(analysisData?.impact_summary || analysisData?.paragraph_summary || analysisData?.summary?.summary_text) && (
          <div className="summary-section" style={{ marginTop: '24px' }}>
            <ImpactSummaryTab 
              summaryData={{
                impact_summary: analysisData.impact_summary,
                paragraph_summary: analysisData.paragraph_summary,
                summary_text: analysisData.impact_summary?.summary_text || analysisData.summary?.summary_text,
                ...analysisData.impact_summary,
                ...analysisData
              }} 
            />
          </div>
        )}

        {/* Debug info to help troubleshoot */}
        <details
          style={{
            marginTop: "20px",
            fontSize: "14px",
            color: "var(--text-light)",
          }}
        >
          <summary>Debug Info (click to expand)</summary>
          <pre>{JSON.stringify(analysisData, null, 2)}</pre>
        </details>
      </div>
    );
  };

  const renderKnowledgeGraph = () => {
    const kgData = {
      old_kg_triplets: analysisData?.old_kg_triplets || [],
      new_kg_triplets: analysisData?.new_kg_triplets || [],
      kg_comparison: analysisData?.kg_comparison || {},
    };

    return <KnowledgeGraph analysisData={kgData} sessionId={sessionId} />;
  };

  return (
    <div className="analysis-results">
      <div className="results-header">
        <h2>{/* {isConversion ? ' Python Conversion Results' :} */}</h2>
        <div className="results-tabs">
          <button
            className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          {!isConversion && (
            <>
              <button
                className={`tab-button ${
                  activeTab === "impact" ? "active" : ""
                }`}
                onClick={() => setActiveTab("impact")}
              >
                Impact Analysis
              </button>
              <button
                className={`tab-button ${
                  activeTab === "dependencies" ? "active" : ""
                }`}
                onClick={() => setActiveTab("dependencies")}
              >
                Dependencies
              </button>
              <button
                className={`tab-button ${
                  activeTab === "knowledge" ? "active" : ""
                }`}
                onClick={() => setActiveTab("knowledge")}
              >
                Knowledge Graph
              </button>
            </>
          )}
          {isConversion && (
            <button
              className={`tab-button ${
                activeTab === "conversion" ? "active" : ""
              }`}
              onClick={() => setActiveTab("conversion")}
            >
              Conversion
            </button>
          )}
        </div>
      </div>

      <div className="results-content">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "impact" && renderImpactAnalysis()}
        {activeTab === "dependencies" && renderDependencyAnalysis()}
        {activeTab === "conversion" && renderPythonConversion()}
        {activeTab === "knowledge" && renderKnowledgeGraph()}
      </div>
    </div>
  );
};

export default AnalysisResults;
